import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Switch, Button, Modal, Input, Select, Divider, Tag, Empty, Spin, Tooltip, Avatar } from 'antd';
import {
  PlusOutlined,
  SettingOutlined,
  MailOutlined,
  SafetyCertificateOutlined,
  SwapOutlined,
  DeleteOutlined,
  CheckOutlined,
  EditOutlined,
  CloseOutlined,
  SearchOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { DetailsCard } from '../helpers/Components';
import MyTable from '../helpers/MyTable';
import SubPageLayout from '../helpers/SubPageLayout';

import UsersAdd from './UsersAdd';
import UserEdit from './UsersEdit';

import {
  fetchRoles,
  fetchRoleActions,
  getRoles,
  deleteRole,
  getRolesLoading,
  getRoleActions,
  getRoleActionsLoading,
  addNewRole,
  getUsersList,
  getUsersLoading,
  getUserTableInfo,
  updateUser,
  fetchRoleById,
  updateRole,
  getEditingRole,
  getEditingRoleLoading,
  getAllUsersForLocalFilter,
} from '../../../features/user.store';

import { getUserRole, isFeatureEnabled } from '../../../features/auth.store';
import { DEFAULT_PAGE_SIZE } from '../../../utils/constant';
import { ROLE } from '../../../utils/features';

import { toast } from 'react-toastify';

/** Role Types (UI enum) */
export const ROLE_TYPES = {
  SITE: 'SITE_ROLE',
  TENANT: 'TENANT_ROLE',
  MINEARC: 'MINEARC_ROLE',
  SUPER_ADMIN: 'SUPER_ADMIN_ROLE',
};

export const ROLE_TYPE_OPTIONS = [
  { label: 'Site Role', value: ROLE_TYPES.SITE },
  { label: 'Tenant Role', value: ROLE_TYPES.TENANT },
  { label: 'MineARC Role', value: ROLE_TYPES.MINEARC },
  { label: 'Super Admin Role', value: ROLE_TYPES.SUPER_ADMIN },
];

/**
 * Treat Super Admin and MineARC as same for VIEW / UI ACCESS in this page
 */
const hasSuperAdminViewAccess = (userRole) => {
  return [
    ROLE?.SA,
    ROLE?.SUPER_ADMIN,
    ROLE?.MINEARC,
    ROLE?.MINEARC_ROLE,
  ].includes(userRole);
};

/**
 * =======================================================
 * Drag & Drop Dual List for Actions
 * =======================================================
 */
const DraggableActionBoard = ({ allActions = [], selectedActions = [], setSelectedActions }) => {
  const [searchAll, setSearchAll] = useState('');
  const [searchSelected, setSearchSelected] = useState('');
  const draggedItemRef = useRef(null);

  const availableActions = useMemo(() => {
    const selectedSet = new Set(selectedActions);
    const remaining = allActions.filter((a) => !selectedSet.has(a));

    if (!searchAll.trim()) return remaining;
    const q = searchAll.toLowerCase();
    return remaining.filter((a) => a.toLowerCase().includes(q));
  }, [allActions, selectedActions, searchAll]);

  const filteredSelected = useMemo(() => {
    if (!searchSelected.trim()) return selectedActions;
    const q = searchSelected.toLowerCase();
    return selectedActions.filter((a) => a.toLowerCase().includes(q));
  }, [selectedActions, searchSelected]);

  const addAction = (action) => {
    if (!selectedActions.includes(action)) setSelectedActions([...selectedActions, action]);
  };

  const removeAction = (action) => {
    setSelectedActions(selectedActions.filter((a) => a !== action));
  };

  const moveSelected = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= selectedActions.length) return;
    const copy = [...selectedActions];
    const [item] = copy.splice(fromIndex, 1);
    copy.splice(toIndex, 0, item);
    setSelectedActions(copy);
  };

  const onDragStart = (action, source, index) => {
    draggedItemRef.current = { action, source, index };
  };

  const onDropToAvailable = () => {
    const item = draggedItemRef.current;
    if (!item) return;
    if (item.source === 'selected') removeAction(item.action);
    draggedItemRef.current = null;
  };

  const onDropToSelected = () => {
    const item = draggedItemRef.current;
    if (!item) return;
    if (item.source === 'available') addAction(item.action);
    draggedItemRef.current = null;
  };

  const onDropReorderSelected = (dropIndex) => {
    const item = draggedItemRef.current;
    if (!item) return;

    if (item.source === 'selected') {
      moveSelected(item.index, dropIndex);
    } else {
      const action = item.action;
      if (!selectedActions.includes(action)) {
        const copy = [...selectedActions];
        copy.splice(dropIndex, 0, action);
        setSelectedActions(copy);
      }
    }

    draggedItemRef.current = null;
  };

  const addAll = () => setSelectedActions(allActions);
  const removeAll = () => setSelectedActions([]);

  return (
    <div style={styles.dragBoard}>
      <div style={styles.dragPanel}>
        <div style={styles.dragPanelHeader}>
          <div>
            <div style={styles.dragPanelTitle}>All Actions</div>
            <div style={styles.dragPanelSub}>{availableActions.length} available</div>
          </div>

          <Button size="small" onClick={addAll} icon={<CheckOutlined />}>
            Add All
          </Button>
        </div>

        <Input
          value={searchAll}
          onChange={(e) => setSearchAll(e.target.value)}
          placeholder="Search actions..."
          style={{ marginBottom: 10 }}
        />

        <div style={styles.dragList} onDragOver={(e) => e.preventDefault()} onDrop={onDropToAvailable}>
          {availableActions.length === 0 ? (
            <div style={styles.emptyList}>
              <Empty description="No actions available" />
            </div>
          ) : (
            availableActions.map((action) => (
              <div
                key={action}
                draggable
                onDragStart={() => onDragStart(action, 'available')}
                onDoubleClick={() => addAction(action)}
                style={styles.actionItem}
              >
                <span style={styles.actionText}>{action}</span>
                <span style={styles.hint}>Drag →</span>
              </div>
            ))
          )}
        </div>
      </div>

      <div style={styles.dragMiddle}>
        <div style={styles.middleCard}>
          <SwapOutlined style={{ fontSize: 26, opacity: 0.65 }} />
          <div style={{ fontWeight: 700, marginTop: 8 }}>Drag & Drop</div>
          <div style={{ opacity: 0.75, fontSize: 12, marginTop: 4, textAlign: 'center' }}>
            Drag actions between lists.
            <br />
            Double-click to add/remove.
          </div>
        </div>
      </div>

      <div style={styles.dragPanel}>
        <div style={styles.dragPanelHeader}>
          <div>
            <div style={styles.dragPanelTitle}>Role Actions</div>
            <div style={styles.dragPanelSub}>{selectedActions.length} selected</div>
          </div>

          <Button size="small" danger onClick={removeAll} icon={<DeleteOutlined />}>
            Clear
          </Button>
        </div>

        <Input
          value={searchSelected}
          onChange={(e) => setSearchSelected(e.target.value)}
          placeholder="Search selected..."
          style={{ marginBottom: 10 }}
        />

        <div style={styles.dragList} onDragOver={(e) => e.preventDefault()} onDrop={onDropToSelected}>
          {filteredSelected.length === 0 ? (
            <div style={styles.emptyList}>
              <Empty description="No actions selected" />
              <div style={{ marginTop: 8, opacity: 0.7, fontSize: 12 }}>Drag actions here to assign permissions.</div>
            </div>
          ) : (
            filteredSelected.map((action, idx) => {
              const actualIndex = selectedActions.indexOf(action);
              return (
                <div
                  key={`${action}_${idx}`}
                  draggable
                  onDragStart={() => onDragStart(action, 'selected', actualIndex)}
                  onDoubleClick={() => removeAction(action)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => onDropReorderSelected(actualIndex)}
                  style={{
                    ...styles.actionItem,
                    border: '1px solid rgba(11, 106, 59, 0.22)',
                    background: 'rgba(11, 106, 59, 0.06)',
                  }}
                >
                  <span style={styles.actionText}>
                    <Tag color="green" style={{ marginRight: 10, fontSize: 11 }}>
                      {actualIndex + 1}
                    </Tag>
                    {action}
                  </span>

                  <span style={styles.hint}>← Drag</span>
                </div>
              );
            })
          )}
        </div>

        <div style={styles.bottomHint}>✅ Tip: Drag inside this list to reorder. Double click to remove.</div>
      </div>
    </div>
  );
};

/**
 * Roles Manager Modal
 */
const RolesManager = ({
  open,
  onClose,
  roles = [],
  rolesLoading,
  allActions = [],
  actionsLoading,
  userRole,
}) => {
  const dispatch = useDispatch();

  const editingRole = useSelector(getEditingRole);
  const editingRoleLoading = useSelector(getEditingRoleLoading);

  const [mode, setMode] = useState('LIST');
  const [editingRoleId, setEditingRoleId] = useState(null);

  const [roleName, setRoleName] = useState('');
  const [roleType, setRoleType] = useState(ROLE_TYPES.TENANT);
  const [roleActions, setRoleActions] = useState([]);
  const [roleDisplayName, setRoleDisplayName] = useState('');
  const [roleDescription, setRoleDescription] = useState('');
  const [roleSearch, setRoleSearch] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, role: null });

  const canManageRoles = hasSuperAdminViewAccess(userRole);

  const resetForm = () => {
    setRoleName('');
    setRoleDisplayName('');
    setRoleDescription('');
    setRoleType(ROLE_TYPES.TENANT);
    setRoleActions([]);
    setEditingRoleId(null);
  };

  const openCreate = () => {
    if (!canManageRoles) {
      toast.error('Only Super Admin or MineARC Role can manage roles.');
      return;
    }
    resetForm();
    setMode('CREATE');
  };

  const isSuperAdminRole = (role) => {
    if (!role) return false;
    const type = (role.type || '').toUpperCase();
    const name = (role.name || '').toUpperCase();
    const displayName = (role.displayName || role.display_name || '').toUpperCase();

    return (
      type === 'SUPER_ADMIN_ROLE' ||
      name === 'ROLE_SUPER_ADMIN' ||
      displayName === 'SUPER ADMIN ROLE'
    );
  };

  const isTenantAdminRole = (role) => {
    if (!role) return false;
    const name = (role.name || '').trim().toUpperCase();
    const displayName = (role.displayName || role.display_name || '').trim().toUpperCase();

    return name === 'TENANT ADMIN ROLE' || displayName === 'TENANT ADMIN ROLE';
  };

  const isProtectedRole = (role) => isSuperAdminRole(role) || isTenantAdminRole(role);

  const confirmDeleteRole = (role) => {
    if (!role?.id) return;

    if (!canManageRoles) {
      toast.error("You don't have permission to delete roles.");
      return;
    }

    if (isSuperAdminRole(role)) {
      toast.error('You cannot delete the Super Admin Role.');
      return;
    }

    if (isTenantAdminRole(role)) {
      toast.error('You cannot delete the Tenant Admin Role.');
      return;
    }

    setDeleteConfirm({ open: true, role });
  };

  const handleDeleteRole = async () => {
    const role = deleteConfirm.role;
    if (!role?.id) return;

    try {
      await dispatch(deleteRole({ roleId: role.id })).unwrap();
      toast.success('Role deleted successfully');
      await dispatch(fetchRoles());

      if (editingRoleId === role.id) {
        setMode('LIST');
        resetForm();
      }
    } catch (err) {
      console.error(err);
      if (err?.status === 403) toast.error("You don't have permission to delete roles.");
      else toast.error(err?.data?.message || err?.message || 'You do not have permission to perform this action!');
    } finally {
      setDeleteConfirm({ open: false, role: null });
    }
  };

  const openEdit = async (role) => {
    const id = role?.id;
    if (!id) return;

    if (!canManageRoles) {
      toast.error("You don't have permission to edit roles.");
      return;
    }

    setEditingRoleId(id);
    setMode('EDIT');

    const res = await dispatch(fetchRoleById({ roleId: id })).unwrap();

    setRoleName(res?.name || '');
    setRoleDisplayName(res?.displayName || res?.display_name || res?.name || '');
    setRoleDescription(res?.description || '');
    setRoleType(res?.type || ROLE_TYPES.TENANT);
    setRoleActions(res?.actions || []);
  };

  const editingRoleData = useMemo(
    () => roles.find((r) => String(r.id) === String(editingRoleId)),
    [roles, editingRoleId]
  );

  const isEditingProtectedName = mode === 'EDIT' && isTenantAdminRole(editingRoleData);

  const handleSaveRole = async () => {
    if (!canManageRoles) {
      toast.error('Only Super Admin or MineARC Role can manage roles.');
      return;
    }

    if (!roleName.trim()) return toast.error('Role name is required');
    if (!roleType) return toast.error('Role type is required');
    if (!roleActions?.length) return toast.error('Select at least one action');

    const payload = {
      name: isEditingProtectedName ? editingRoleData?.name || roleName.trim() : roleName.trim(),
      displayName: (roleDisplayName || roleName).trim(),
      description: roleDescription?.trim() || null,
      type: roleType,
      actions: roleActions,
    };

    try {
      if (mode === 'CREATE') {
        await dispatch(addNewRole({ formValue: payload })).unwrap();
        toast.success('Role created successfully');
      } else if (mode === 'EDIT') {
        if (!editingRoleId) return toast.error('No role selected');
        await dispatch(updateRole({ roleId: editingRoleId, formValue: payload })).unwrap();
        toast.success('Role updated successfully');
      }

      await dispatch(fetchRoles());
      setMode('LIST');
      resetForm();
    } catch (err) {
      console.error(err);
      if (err?.status === 403) toast.error("You don't have permission to create or update roles.");
      else toast.error(err?.data?.message || err?.message || 'You do not have permission to perform this action!');
    }
  };

  const filteredRoles = useMemo(() => {
    if (!roleSearch.trim()) return roles;
    const q = roleSearch.toLowerCase();
    return roles.filter((r) => (r.name || '').toLowerCase().includes(q));
  }, [roles, roleSearch]);

  return (
    <Modal
      visible={open}
      width={1160}
      footer={null}
      destroyOnClose
      onCancel={() => {
        onClose();
        setMode('LIST');
        resetForm();
      }}
      bodyStyle={{ padding: 0, borderRadius: 16 }}
      style={{ top: 22 }}
    >
      <div style={styles.modalHeader}>
        <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
          <div style={styles.headerIcon}>
            <SafetyCertificateOutlined style={{ fontSize: 20, color: '#0B6A3B' }} />
          </div>
          <div>
            <div style={styles.headerTitle}>Role Manager</div>
            <div style={styles.headerSubtitle}>Drag actions to build a role. Assign it to users to control access.</div>
          </div>
        </div>

        {mode === 'LIST' ? (
          canManageRoles ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Create Role
            </Button>
          ) : null
        ) : (
          <div style={{ display: 'flex', gap: 10 }}>
            <Button
              onClick={() => {
                setMode('LIST');
                resetForm();
              }}
              icon={<CloseOutlined />}
            >
              Back
            </Button>
            <Button type="primary" onClick={handleSaveRole}>
              Save Role
            </Button>
          </div>
        )}
      </div>

      <Divider style={{ margin: 0 }} />

      <div style={styles.modalBody}>
        <div style={styles.leftPanel}>
          <div style={styles.panelTitle}>Roles</div>

          <Input
            value={roleSearch}
            onChange={(e) => setRoleSearch(e.target.value)}
            placeholder="Search role..."
            style={{ marginBottom: 12 }}
            allowClear
          />

          <div style={styles.roleList}>
            {rolesLoading ? (
              <div style={{ padding: 14 }}>
                <Spin spinning />
              </div>
            ) : filteredRoles.length === 0 ? (
              <Empty description="No roles found" />
            ) : (
              filteredRoles.map((role) => {
                const isSelected = editingRoleId && role.id === editingRoleId;
                const roleLabel = ROLE_TYPE_OPTIONS.find((x) => x.value === role.type)?.label || role.type;
                const nameToShow = role.displayName || role.display_name || role.name || 'Unnamed Role';
                const actionsCount = Array.isArray(role.actions) ? role.actions.length : 0;
                const deleteDisabled = isProtectedRole(role);
                const deleteTooltip = isSuperAdminRole(role)
                  ? 'Cannot delete Super Admin Role'
                  : isTenantAdminRole(role)
                    ? 'Cannot delete Tenant Admin Role'
                    : 'Delete';

                return (
                  <div
                    key={role.id || role.name}
                    style={{
                      ...styles.roleCard,
                      ...(isSelected ? styles.roleCardSelected : {}),
                    }}
                    onClick={() => openEdit(role)}
                  >
                    <div style={styles.roleCardTop}>
                      <div style={styles.roleCardTitleWrap}>
                        <div style={styles.roleCardTitle} title={nameToShow}>
                          {nameToShow}
                        </div>
                        {role.name && role.displayName && role.name !== role.displayName ? (
                          <div style={styles.roleCardSub} title={role.name}>
                            {role.name}
                          </div>
                        ) : null}
                      </div>

                      {canManageRoles ? (
                        <div style={styles.roleCardButtons}>
                          <Tooltip title="Edit">
                            <Button
                              size="small"
                              type="default"
                              icon={<EditOutlined />}
                              onClick={(e) => {
                                e.stopPropagation();
                                openEdit(role);
                              }}
                            />
                          </Tooltip>

                          <Tooltip title={deleteTooltip}>
                            <Button
                              size="small"
                              danger
                              icon={<DeleteOutlined />}
                              disabled={deleteDisabled}
                              onClick={(e) => {
                                e.stopPropagation();
                                confirmDeleteRole(role);
                              }}
                            />
                          </Tooltip>
                        </div>
                      ) : null}
                    </div>

                    <div style={styles.roleCardMetaRow}>
                      <Tag style={styles.roleTag}>{roleLabel}</Tag>
                      <div style={styles.roleMetaText}>
                        {actionsCount} action{actionsCount === 1 ? '' : 's'}
                      </div>
                    </div>

                    {role.description ? (
                      <div style={styles.roleCardDesc} title={role.description}>
                        {role.description}
                      </div>
                    ) : (
                      <div style={styles.roleCardDescMuted}>No description</div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div style={styles.rightPanel}>
          {mode === 'LIST' && (
            <div style={styles.placeholderCard}>
              <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>Select a role to edit</div>
              <div style={{ opacity: 0.75, marginBottom: 16 }}>
                {canManageRoles ? (
                  <>
                    Or click <b>Create Role</b> to build a new one.
                  </>
                ) : (
                  <>You do not have permission to manage roles.</>
                )}
              </div>
              {canManageRoles ? (
                <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                  Create Role
                </Button>
              ) : null}
            </div>
          )}

          {(mode === 'CREATE' || mode === 'EDIT') && (
            <Spin spinning={mode === 'EDIT' && editingRoleLoading}>
              <div style={{ display: 'grid', gap: 14 }}>
                <div style={styles.editorTop}>
                  <div>
                    <div style={styles.editorTitle}>{mode === 'CREATE' ? 'Create Role' : 'Edit Role'}</div>
                    <div style={styles.editorSubtitle}>Define fields, then drag actions to the Role Actions list.</div>
                  </div>
                </div>

                <div style={styles.formGrid}>
                  <div>
                    <div style={styles.label}>Role Name</div>
                    <Input
                      value={roleName}
                      onChange={(e) => setRoleName(e.target.value)}
                      placeholder="e.g. Office Manager"
                      size="large"
                      disabled={isEditingProtectedName}
                    />
                  </div>

                  <div>
                    <div style={styles.label}>Role Type</div>

                    {mode === 'CREATE' ? (
                      <Select
                        value={roleType}
                        onChange={setRoleType}
                        style={{ width: '100%' }}
                        options={ROLE_TYPE_OPTIONS}
                        size="large"
                        placeholder="Select role type"
                      />
                    ) : (
                      <Input
                        value={ROLE_TYPE_OPTIONS.find((x) => x.value === roleType)?.label || roleType || '-'}
                        size="large"
                        disabled
                      />
                    )}
                  </div>

                  <div style={{ gridColumn: '1 / -1' }}>
                    <div style={styles.label}>Description</div>
                    <Input.TextArea
                      value={roleDescription}
                      onChange={(e) => setRoleDescription(e.target.value)}
                      placeholder="What can this role do?"
                      rows={3}
                    />
                  </div>

                  <div style={{ gridColumn: '1 / -1' }}>
                    <div style={styles.label}>Display Name (optional)</div>
                    <Input
                      value={roleDisplayName}
                      onChange={(e) => setRoleDisplayName(e.target.value)}
                      placeholder="Human readable name (defaults to Role Name)"
                      size="large"
                    />
                  </div>
                </div>

                {actionsLoading ? (
                  <Spin spinning />
                ) : (
                  <DraggableActionBoard
                    allActions={allActions}
                    selectedActions={roleActions}
                    setSelectedActions={setRoleActions}
                  />
                )}
              </div>
            </Spin>
          )}
        </div>
      </div>

      <Modal
        title="Delete Role"
        visible={deleteConfirm.open}
        onOk={handleDeleteRole}
        onCancel={() => setDeleteConfirm({ open: false, role: null })}
        okButtonProps={{ danger: true }}
        okText="Delete"
        cancelText="Cancel"
        destroyOnClose
        centered
        zIndex={2000}
        getContainer={false}
      >
        {deleteConfirm.role ? (
          <div style={{ lineHeight: 1.5 }}>
            Are you sure you want to delete the role <b>{deleteConfirm.role.displayName || deleteConfirm.role.name}</b>?
            <div style={{ marginTop: 8, opacity: 0.75 }}>
              This will remove all assigned actions for this role.
            </div>
          </div>
        ) : null}
      </Modal>
    </Modal>
  );
};

const columns = (dispatch, { handleChangeOrder, userRole, roles, localAscending, onUserChanged, onViewUser }) => {
  const canViewAsAdmin = hasSuperAdminViewAccess(userRole);

  const columnsList = [
    {
      title: 'User Name',
      dataIndex: 'fullName',
      render: (name, record) => (
        <DetailsCard
          src={record.profilePictureURL}
          name={name}
          id={record.id}
          className="d-flex f-row ai-center"
          as="div"
        />
      ),
      sorter: true,
      sortDirections: [localAscending ? 'descend' : 'ascend'],
      onHeaderCell: () => ({ onClick: handleChangeOrder }),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      render: (value) => value || '-',
    },
    { title: 'Phone Number', dataIndex: 'telephoneNumber' },
    {
      title: 'Role',
      dataIndex: ['role', 'displayName'],
      render: (val, record) => val || record?.role?.name || '-',
    },
    {
      title: 'Enabled',
      dataIndex: 'isLocked',
      render: (data, record) => (
        <Switch
          checked={!data}
          onChange={() => dispatch(updateUser({ userId: record.id, body: { isLocked: !data } }))}
        />
      ),
    },
  ];

  if (canViewAsAdmin) {
    columnsList.push({
      title: 'Actions',
      render: (data, record) => (
        <div className="users-row-actions">
          <Tooltip title="View user">
            <Button
              type="link"
              className="users-view-action"
              aria-label={`View ${record.fullName && record.fullName !== '-' ? record.fullName : record.email || 'user'}`}
              icon={<EyeOutlined />}
              onClick={(event) => {
                event.stopPropagation();
                onViewUser(record);
              }}
            />
          </Tooltip>
          <UserEdit
            disabled={!canViewAsAdmin}
            id={record.id}
            fullname={record.fullName}
            roles={roles}
            onUserChanged={onUserChanged}
          />
        </div>
      ),
    });
  } else {
    columnsList.push({
      title: 'Actions',
      render: (_, record) => (
        <Tooltip title="View user">
          <Button
            type="link"
            className="users-view-action"
            aria-label={`View ${record.fullName && record.fullName !== '-' ? record.fullName : record.email || 'user'}`}
            icon={<EyeOutlined />}
            onClick={() => onViewUser(record)}
          />
        </Tooltip>
      ),
    });
  }

  return columnsList;
};

const Users = () => {
  const getFeatureEnabled = useSelector(isFeatureEnabled);
  const featureAddUsers = getFeatureEnabled('add_organizations');

  const dispatch = useDispatch();
  const history = useHistory();
  const userTableInfo = useSelector(getUserTableInfo);
  const allUsers = useSelector(getUsersList);
  const userListLoading = useSelector(getUsersLoading);
  const userRole = useSelector(getUserRole);

  const roles = useSelector(getRoles);
  const rolesLoading = useSelector(getRolesLoading);

  const roleActionsRaw = useSelector(getRoleActions);
  const roleActionsLoading = useSelector(getRoleActionsLoading);

  const canViewAsAdmin = hasSuperAdminViewAccess(userRole);

  const roleActions = useMemo(() => {
    if (Array.isArray(roleActionsRaw) && typeof roleActionsRaw[0] === 'string') return roleActionsRaw;
    return (roleActionsRaw || []).map((a) => a.actionName).filter(Boolean);
  }, [roleActionsRaw]);

  const [openRoleManager, setOpenRoleManager] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedRoleFilter, setSelectedRoleFilter] = useState('ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const [localAscending, setLocalAscending] = useState(true);
  const [viewingUser, setViewingUser] = useState(null);

  const reloadUsers = useCallback(async () => {
    await dispatch(getAllUsersForLocalFilter());
  }, [dispatch]);

  useEffect(() => {
    reloadUsers();
    dispatch(fetchRoles());
  }, [dispatch, userRole, reloadUsers]);

  const roleFilterOptions = useMemo(() => {
    const roleMap = new Map();

    (roles || []).forEach((role) => {
      if (role?.id) {
        roleMap.set(String(role.id), {
          label: role.displayName || role.display_name || role.name || 'Unnamed Role',
          value: String(role.id),
        });
      }
    });

    (allUsers || []).forEach((user) => {
      const roleId = user?.role?.id;
      if (roleId && !roleMap.has(String(roleId))) {
        roleMap.set(String(roleId), {
          label: user?.role?.displayName || user?.role?.name || 'Unnamed Role',
          value: String(roleId),
        });
      }
    });

    return [{ label: 'All Roles', value: 'ALL' }, ...Array.from(roleMap.values())];
  }, [roles, allUsers]);

  const filteredUsers = useMemo(() => {
    let list = [...(allUsers || [])];

    if (searchText.trim()) {
      const q = searchText.toLowerCase().trim();

      list = list.filter((user) => {
        const fullName = user?.fullName?.toLowerCase() || '';
        const phone = user?.telephoneNumber?.toLowerCase() || '';
        const email = user?.email?.toLowerCase() || '';
        const roleName = user?.role?.displayName?.toLowerCase() || user?.role?.name?.toLowerCase() || '';

        return fullName.includes(q) || phone.includes(q) || email.includes(q) || roleName.includes(q);
      });
    }

    if (selectedRoleFilter !== 'ALL') {
      list = list.filter((user) => String(user?.role?.id) === String(selectedRoleFilter));
    }

    list.sort((a, b) => {
      const aName = (a?.fullName || '').toLowerCase();
      const bName = (b?.fullName || '').toLowerCase();

      if (localAscending) {
        return aName.localeCompare(bName);
      }

      return bName.localeCompare(aName);
    });

    return list;
  }, [allUsers, searchText, selectedRoleFilter, localAscending]);

  const paginatedUsers = useMemo(() => {
    const start = (currentPage - 1) * DEFAULT_PAGE_SIZE;
    const end = start + DEFAULT_PAGE_SIZE;
    return filteredUsers.slice(start, end);
  }, [filteredUsers, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, selectedRoleFilter]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filteredUsers.length / DEFAULT_PAGE_SIZE));
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [filteredUsers.length, currentPage]);

  const handleChangeOrder = () => {
    setLocalAscending((prev) => !prev);
  };

  return (
    <SubPageLayout
      title="Users"
      subtitle=""
      extra={
        <div className="d-flex" style={{ gap: 10 }}>
          <Button
            icon={<MailOutlined />}
            onClick={() => history.push('/users/manage-emails')}
          >
            Manage Emails
          </Button>
          <Button
            icon={<SettingOutlined />}
            onClick={() => {
              setOpenRoleManager(true);
              dispatch(fetchRoles());
              dispatch(fetchRoleActions());
            }}
          >
            Manage Roles
          </Button>
          {featureAddUsers && canViewAsAdmin && <UsersAdd roles={roles} />}
        </div>
      }
    >
      <div style={styles.filterBar}>
        <div style={styles.searchWrap}>
          <Input
            allowClear
            size="large"
            prefix={<SearchOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />}
            placeholder="Search Users"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>

        <div style={styles.roleFilterWrap}>
          <Select
            size="large"
            value={selectedRoleFilter}
            onChange={setSelectedRoleFilter}
            options={roleFilterOptions}
            style={{ width: '100%' }}
            placeholder="Filter by Role"
          />
        </div>
      </div>

      <MyTable
        columns={columns(dispatch, {
          handleChangeOrder,
          userRole,
          roles,
          localAscending,
          onUserChanged: reloadUsers,
          onViewUser: setViewingUser,
        })}
        pagination={{
          position: ['none', 'bottomCenter'],
          pageSize: DEFAULT_PAGE_SIZE,
          total: filteredUsers.length,
          current: currentPage,
        }}
        onChange={(pagination) => setCurrentPage(pagination.current)}
        loading={userListLoading}
        rowKey="id"
        dataSource={paginatedUsers}
      />

      <Modal
        title="User Details"
        visible={Boolean(viewingUser)}
        footer={null}
        onCancel={() => setViewingUser(null)}
        width={640}
        wrapClassName="user-details-modal-wrap"
        destroyOnClose
      >
        {viewingUser && (
          <div className="user-details-content">
            <div className="user-details-heading">
              <Avatar
                size={56}
                src={viewingUser.profilePictureURL}
                alt={viewingUser.fullName && viewingUser.fullName !== '-' ? viewingUser.fullName : viewingUser.email || 'User'}
              >
                {(viewingUser.fullName && viewingUser.fullName !== '-'
                  ? viewingUser.fullName
                  : viewingUser.email || 'U').charAt(0).toUpperCase()}
              </Avatar>
              <div className="user-details-heading-copy">
                <h2>
                  {viewingUser.fullName && viewingUser.fullName !== '-'
                    ? viewingUser.fullName
                    : viewingUser.email || `User ${viewingUser.id || ''}`}
                </h2>
                <span>{viewingUser.role?.displayName || viewingUser.role?.name || 'No role assigned'}</span>
              </div>
              <span className={`user-details-status ${viewingUser.isLocked ? 'locked' : 'enabled'}`}>
                {viewingUser.isLocked ? 'Disabled' : 'Enabled'}
              </span>
            </div>

            <div className="user-details-grid">
              <div className="user-details-item">
                <span>Email</span>
                <strong>{viewingUser.email || '-'}</strong>
              </div>
              <div className="user-details-item">
                <span>Phone number</span>
                <strong>{viewingUser.telephoneNumber || '-'}</strong>
              </div>
              <div className="user-details-item">
                <span>Company</span>
                <strong>{viewingUser.tenant?.name || viewingUser.tenantName || '-'}</strong>
              </div>
              <div className="user-details-item">
                <span>Assigned sites</span>
                <strong>
                  {Array.isArray(viewingUser.assignedSites) && viewingUser.assignedSites.length
                    ? viewingUser.assignedSites.map((site) => site.name).filter(Boolean).join(', ') || `${viewingUser.assignedSites.length} assigned`
                    : '-'}
                </strong>
              </div>
              <div className="user-details-item">
                <span>Certification number</span>
                <strong>{viewingUser.certificationNumber || '-'}</strong>
              </div>
              <div className="user-details-item">
                <span>Certification expiry</span>
                <strong>{viewingUser.certificationExpiry ? String(viewingUser.certificationExpiry) : '-'}</strong>
              </div>
            </div>
          </div>
        )}
      </Modal>

      <RolesManager
        open={openRoleManager}
        onClose={() => setOpenRoleManager(false)}
        roles={roles}
        rolesLoading={rolesLoading}
        allActions={roleActions}
        actionsLoading={roleActionsLoading}
        userRole={userRole}
      />
    </SubPageLayout>
  );
};

export default Users;

/**
 * Styles
 */
const styles = {
  filterBar: {
    display: 'flex',
    gap: 12,
    marginBottom: 16,
    flexWrap: 'wrap',
  },

  searchWrap: {
    flex: 1,
    minWidth: 280,
    maxWidth: 460,
  },

  roleFilterWrap: {
    width: 260,
  },

  modalHeader: {
    padding: '18px 22px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: '#F8FAF9',
  },
  headerIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    background: 'rgba(11, 106, 59, 0.12)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: { fontSize: 18, fontWeight: 800 },
  headerSubtitle: { opacity: 0.75, marginTop: 2 },

  modalBody: {
    display: 'grid',
    gridTemplateColumns: '380px 1fr',
    minHeight: 680,
  },

  leftPanel: {
    borderRight: '1px solid #f0f0f0',
    padding: 16,
    background: '#fff',
    overflowX: 'hidden',
  },
  rightPanel: {
    padding: 18,
    background: '#fbfbfb',
  },

  panelTitle: { fontSize: 14, fontWeight: 800, marginBottom: 10 },

  roleList: {
    display: 'grid',
    gap: 10,
    maxHeight: 585,
    overflowY: 'auto',
    paddingRight: 6,
  },

  roleCard: {
    background: '#fff',
    border: '1px solid #eaeaea',
    borderRadius: 14,
    padding: 12,
    display: 'grid',
    gap: 8,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    boxShadow: '0 1px 0 rgba(0,0,0,0.02)',
  },
  roleCardSelected: {
    border: '1px solid rgba(11, 106, 59, 0.35)',
    background: 'rgba(11, 106, 59, 0.04)',
  },
  roleCardTop: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 10,
  },
  roleCardTitleWrap: {
    minWidth: 0,
    flex: 1,
  },
  roleCardTitle: {
    fontSize: 14,
    fontWeight: 900,
    lineHeight: 1.2,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  roleCardSub: {
    marginTop: 4,
    fontSize: 12,
    opacity: 0.7,
    lineHeight: 1.2,
    display: '-webkit-box',
    WebkitLineClamp: 1,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  roleCardButtons: {
    flexShrink: 0,
    display: 'flex',
    gap: 8,
    alignItems: 'center',
  },
  roleCardMetaRow: {
    display: 'flex',
    gap: 10,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  roleTag: {
    margin: 0,
    fontWeight: 700,
  },
  roleMetaText: {
    fontSize: 12,
    opacity: 0.75,
    fontWeight: 700,
  },
  roleCardDesc: {
    fontSize: 12,
    opacity: 0.85,
    lineHeight: 1.3,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  roleCardDescMuted: {
    fontSize: 12,
    opacity: 0.5,
    fontStyle: 'italic',
  },

  placeholderCard: {
    background: '#fff',
    border: '1px solid #eee',
    borderRadius: 14,
    padding: 22,
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    textAlign: 'center',
  },

  editorTop: {
    background: '#fff',
    border: '1px solid #eee',
    borderRadius: 14,
    padding: 16,
  },
  editorTitle: { fontSize: 18, fontWeight: 900 },
  editorSubtitle: { opacity: 0.75, marginTop: 2 },

  formGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
  },
  label: { fontWeight: 700, marginBottom: 6 },

  dragBoard: {
    background: '#fff',
    border: '1px solid #eee',
    borderRadius: 14,
    padding: 14,
    display: 'grid',
    gridTemplateColumns: '1fr 140px 1fr',
    gap: 14,
  },
  dragPanel: {
    border: '1px solid #f0f0f0',
    borderRadius: 12,
    padding: 12,
    background: '#fafafa',
    display: 'grid',
    gridTemplateRows: 'auto auto 1fr auto',
  },
  dragPanelHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 10,
    marginBottom: 10,
  },
  dragPanelTitle: { fontWeight: 900, fontSize: 14 },
  dragPanelSub: { opacity: 0.7, fontSize: 12, marginTop: 2 },

  dragList: {
    border: '1px dashed rgba(0,0,0,0.18)',
    borderRadius: 12,
    padding: 10,
    minHeight: 360,
    maxHeight: 360,
    overflowY: 'auto',
    background: '#fff',
    display: 'grid',
    gap: 8,
  },
  emptyList: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
  },

  actionItem: {
    padding: '10px 10px',
    borderRadius: 12,
    border: '1px solid #eee',
    background: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    cursor: 'grab',
    userSelect: 'none',
  },
  actionText: { fontWeight: 700, fontSize: 12 },
  hint: { opacity: 0.55, fontSize: 12 },

  dragMiddle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  middleCard: {
    width: '100%',
    borderRadius: 14,
    padding: 12,
    background: '#f7f7f7',
    border: '1px solid #eee',
    textAlign: 'center',
  },

  bottomHint: {
    marginTop: 10,
    opacity: 0.72,
    fontSize: 12,
  },
};